import {
  Money04Icon,
  ArrowLeftRightIcon,
  Edit02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonVariant, ButtonSize } from "shared/components";
import { MenuRow } from "views/applications-id/components/LayoutHeader/components/MenuRow";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import {
  BankStatements,
  EditIncomeModalData,
  EditTransactionsModalData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import {
  getIncomeActualNetAndGrossDollars,
  isValidBankStatement
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils";
import { useMemo } from "react";
import { ReverifyAttempt } from "../../../ReverifySlice/types";
import {
  isReverifyTabABankStatement,
  isReverifyTabArchived
} from "../../../ReverifySlice/utils";

const EditButton = ({
  isDisabled,
  onClick,
  disabledReason
}: {
  isDisabled: boolean;
  disabledReason?: string;
  onClick?: () => void;
}) => {
  const getDisabledTooltip = () => {
    if (!isDisabled) return null;
    if (disabledReason) return disabledReason;
    return "You don't have permission for this action";
  };

  return (
    <Button
      variant={ButtonVariant.BASE}
      size={ButtonSize.XS}
      HugeIcon={Edit02Icon}
      className="shadow-xs w-[80px] !gap-2"
      disabled={isDisabled}
      tooltip={getDisabledTooltip()}
      onClick={onClick}
    >
      Edit
    </Button>
  );
};

export const EditIncomeDropdownMenu = ({
  setShowEditIncomeAmountModal,
  setShowEditIncomeTransactionsDrawer,
  selectedReverifyAttempt,
  income,
  allBankStatements,
  disabled,
  disabledReason
}: {
  setShowEditIncomeAmountModal?: (data: false | EditIncomeModalData) => void;
  setShowEditIncomeTransactionsDrawer?: (
    data: false | EditTransactionsModalData
  ) => void;
  selectedReverifyAttempt?: ReverifyAttempt;
  income: CombinedIncome;
  allBankStatements?: BankStatements;
  disabled: boolean;
  disabledReason?: string;
}) => {
  const { permissions } = useAuthContext();
  const { actualVerifiedNetMonthlyDollars, actualVerifiedGrossMonthlyDollars } =
    getIncomeActualNetAndGrossDollars(income, selectedReverifyAttempt);

  const isArchived = isReverifyTabArchived(selectedReverifyAttempt);
  const isBankStatement = isReverifyTabABankStatement(selectedReverifyAttempt);
  const isEditDisabled = disabled || !permissions.canEditIncome;

  const filteredBankStatements = useMemo(
    () =>
      allBankStatements
        ? Object.values(allBankStatements).filter((bankStatement) =>
            isValidBankStatement(bankStatement)
          )
        : [],
    [allBankStatements]
  );

  const isTransactionsEditAvailable = Boolean(
    !isArchived && isBankStatement && filteredBankStatements?.length
  );

  if (!isTransactionsEditAvailable)
    return (
      <EditButton
        isDisabled={!permissions.canEditIncome || disabled}
        disabledReason={disabledReason}
        onClick={() =>
          setShowEditIncomeAmountModal?.({
            grossMonthlyIncomeDollars: actualVerifiedGrossMonthlyDollars,
            netMonthlyIncomeDollars: actualVerifiedNetMonthlyDollars,
            hasNetMonthlyIncome: income.type !== "offer_letter",
            isTotal: false,
            id: income.id,
            additional_data: income.additional_data,
            type: income.type
          })
        }
      />
    );

  const dropdownActions = [
    {
      title: <MenuRow text="Edit income amount" HugeIcon={Money04Icon} />,
      className: "!py-2.5",
      disabled: isEditDisabled,
      tooltip: permissions.canEditIncome
        ? undefined
        : "You don't have permission for this action",
      onClick: () =>
        setShowEditIncomeAmountModal?.({
          grossMonthlyIncomeDollars: actualVerifiedGrossMonthlyDollars,
          netMonthlyIncomeDollars: actualVerifiedNetMonthlyDollars,
          hasNetMonthlyIncome: income.type !== "offer_letter",
          isTotal: false,
          id: income.id,
          additional_data: income.additional_data,
          type: income.type
        })
    },
    {
      title: (
        <MenuRow
          text="Edit income transactions"
          HugeIcon={ArrowLeftRightIcon}
        />
      ),
      className: "!py-2.5",

      disabled: isEditDisabled,
      tooltip: permissions.canEditIncome
        ? undefined
        : "You don't have permission for this action",
      onClick: () =>
        setShowEditIncomeTransactionsDrawer?.({
          id: income.id,
          type: income.type,
          additional_data: income.additional_data
        })
    }
  ];

  return (
    <ContextMenuPopover
      borderLess={false}
      trigger={<EditButton isDisabled={isEditDisabled} />}
      actions={dropdownActions}
      placement="bottom-end"
      contentClassName="w-[275px]"
    />
  );
};
